import { Popover, Tooltip } from 'antd'
import { useMemo, useState } from 'react'
import { Icon } from '@iconify/react'

import styles from '../statusSelector/statusSelector.module.css'

const getTypeIcon = (typeValue) => {
    const normalized = String(typeValue || '')
        .trim()
        .toLowerCase()
    if (normalized === 'bug') {
        return 'solar:bug-outline'
    }
    return 'qlementine-icons:task-16'
}

const getTypeIconColor = (typeValue) => {
    const normalized = String(typeValue || '')
        .trim()
        .toLowerCase()
    if (normalized === 'bug') {
        return '#e63131'
    }
    return '#6d28d9'
}

const TypeSelector = ({
    value,
    onChange,
    name,
    typeOptions = [],
    updateDetails,
    placeholder = 'Select type',
    disabled = false,
    disabledInfoText = 'Type is locked for Issues sprint',
    compact = false,
    dashedBorder = false,
    className,
}) => {
    const [visible, setVisible] = useState(false)

    const handleSelect = (item) => {
        const nextValue = item?.value ?? 'Task'
        onChange?.(nextValue)
        updateDetails?.(name, nextValue)
        setVisible(false)
    }

    const selectedItem = useMemo(() => {
        if (!Array.isArray(typeOptions) || typeOptions.length === 0) return null
        return (
            typeOptions.find(
                (item) =>
                    String(item?.value || '').toLowerCase() ===
                    String(value || '').toLowerCase()
            ) || null
        )
    }, [typeOptions, value])

    const panelContent = (
        <div className={styles.sectionPanel}>
            <div className={styles.sectionPanelInner}>
                <div className={styles.sectionHeader}>{placeholder}</div>
                <div className={styles.sectionScroll}>
                    {typeOptions.map((item, index) => {
                        const isSelected =
                            String(item?.value || '').toLowerCase() ===
                            String(value || '').toLowerCase()

                        return (
                            <div
                                key={
                                    item?.value != null
                                        ? String(item.value)
                                        : `${item?.label || 'type'}-${index}`
                                }
                                role="option"
                                aria-selected={isSelected}
                                className={`${styles.sectionItem} ${
                                    isSelected ? styles.sectionItemSelected : ''
                                }`}
                                onClick={() => handleSelect(item)}
                            >
                                <div className={styles.sectionItemRow}>
                                    <span
                                        className={styles.sectionCheckSlot}
                                        aria-hidden
                                    >
                                        {isSelected ? (
                                            <Icon
                                                icon="mdi:check"
                                                width={18}
                                                height={18}
                                                className={
                                                    styles.sectionCheckIcon
                                                }
                                            />
                                        ) : null}
                                    </span>
                                    <span className={styles.sectionItemLabel}>
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                            }}
                                        >
                                            <Icon
                                                icon={getTypeIcon(item?.value)}
                                                width={16}
                                                height={16}
                                                style={{
                                                    color: getTypeIconColor(
                                                        item?.value
                                                    ),
                                                    flexShrink: 0,
                                                    overflow: 'visible',
                                                    display: 'block',
                                                }}
                                            />
                                            <span>{item?.label || 'Task'}</span>
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )

    const compactTooltipTitle = disabled
        ? disabledInfoText
        : (selectedItem?.label ?? placeholder)

    const typeIconColor = getTypeIconColor(selectedItem?.value)

    const triggerButton = (
        <button
            type="button"
            className={[
                styles.sectionTrigger,
                compact ? styles.sectionTriggerCompact : '',
                compact && dashedBorder
                    ? styles.sectionTriggerCompactDashed
                    : '',
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={visible}
            aria-label={selectedItem?.label ?? placeholder}
            style={{
                ...(compact && disabled ? { pointerEvents: 'none' } : {}),
                ...(compact && dashedBorder
                    ? { borderColor: typeIconColor }
                    : {}),
            }}
        >
            <span className={styles.sectionTriggerInner}>
                {compact ? (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                        }}
                    >
                        <Icon
                            icon={getTypeIcon(selectedItem?.value)}
                            width={16}
                            height={16}
                            style={{
                                color: typeIconColor,
                                flexShrink: 0,
                                overflow: 'visible',
                                display: 'block',
                            }}
                        />
                    </span>
                ) : (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Icon
                            icon={getTypeIcon(selectedItem?.value)}
                            width={16}
                            height={16}
                            style={{
                                color: getTypeIconColor(selectedItem?.value),
                                flexShrink: 0,
                                overflow: 'visible',
                                display: 'block',
                            }}
                        />
                        <span
                            className={`${styles.sectionTriggerText} ${
                                !selectedItem ? styles.sectionPlaceholder : ''
                            }`}
                        >
                            {selectedItem?.label ?? placeholder}
                        </span>
                    </span>
                )}
                {!compact &&
                    (disabled ? (
                        <Tooltip title={disabledInfoText}>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: 'help',
                                }}
                            >
                                <Icon
                                    icon="mdi:information-outline"
                                    width={18}
                                    height={18}
                                    className={styles.sectionChevron}
                                />
                            </span>
                        </Tooltip>
                    ) : (
                        <Icon
                            icon={
                                visible
                                    ? 'mdi:chevron-up'
                                    : 'mdi:chevron-down'
                            }
                            width={20}
                            height={20}
                            className={styles.sectionChevron}
                        />
                    ))}
            </span>
        </button>
    )

    return (
        <Popover
            content={panelContent}
            trigger="click"
            placement="bottomLeft"
            open={disabled ? false : visible}
            onOpenChange={(open) => {
                if (!disabled) setVisible(open)
            }}
            arrow={false}
            styles={{
                root: { paddingTop: 4 },
                body: {
                    padding: 0,
                    background: 'transparent',
                    boxShadow: 'none',
                },
            }}
            getPopupContainer={() => document.body}
        >
            {compact ? (
                <Tooltip title={compactTooltipTitle} placement="top">
                    <span
                        style={{
                            display: 'inline-flex',
                            cursor: disabled ? 'help' : 'pointer',
                        }}
                    >
                        {triggerButton}
                    </span>
                </Tooltip>
            ) : (
                triggerButton
            )}
        </Popover>
    )
}

export default TypeSelector
